/* Modal powered by react-jsonschema-form. */
/* Rendered when the LLM calls ask_form to collect structured data from the user. */

import Form from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import type { RJSFSchema, UiSchema, WidgetProps } from '@rjsf/utils';
import type { IChangeEvent } from '@rjsf/core';
import { useState } from 'react';
import { Dialog } from '@base-ui/react';
import { X } from '@phosphor-icons/react';
import type { WidgetEvent } from '../lib/chat-api';
import { cn } from '@/lib/utils';

/* ── Custom widgets ──────────────────────────────────────────────────────────── */

const optBase = "text-left px-4 py-3 rounded-sm border-[1.5px] border-naia-muted-2 bg-naia-info-bg text-foreground font-semibold text-[14px] cursor-pointer font-[inherit] min-h-[48px] disabled:cursor-default";
const checkboxBase = "flex items-center gap-3 px-4 py-3 rounded-sm border-[1.5px] border-naia-muted-2 bg-naia-info-bg text-foreground font-bold text-[14px] cursor-pointer font-[inherit] w-full text-left min-h-[48px] disabled:cursor-default";
const checkboxBoxBase = "w-5 h-5 rounded-[4px] flex-none grid place-items-center bg-transparent border-2 border-naia-muted-2 text-card text-[13px]";

/* Enum → styled option buttons (replaces default radio inputs). */
/* Label rendered by RJSF FieldTemplate above — not repeated here. */
function ButtonGroupWidget({ options, value, onChange, disabled }: WidgetProps) {
  const enumOptions = (options.enumOptions ?? []) as { value: unknown; label: string }[];
  return (
    <div className="flex flex-col gap-2">
      {enumOptions.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            key={String(opt.value)}
            type="button"
            disabled={disabled}
            onClick={() => onChange(opt.value)}
            className={cn(optBase, selected && "border-[2px] border-naia-blue font-extrabold")}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/* Checkbox → styled toggle row. */
function CheckboxWidget({ value, onChange, disabled, label }: WidgetProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(!value)}
      className={cn(checkboxBase, value && "border-[2px] border-naia-blue")}
    >
      <span className={cn(checkboxBoxBase, value && "bg-naia-blue border-none")}>
        {value ? '✓' : ''}
      </span>
      {label}
    </button>
  );
}

const CUSTOM_WIDGETS = {
  RadioWidget: ButtonGroupWidget,
  SelectWidget: ButtonGroupWidget,
  CheckboxWidget,
};

/* ── Main component ──────────────────────────────────────────────────────────── */

interface Props {
  widget: WidgetEvent;
  disabled?: boolean;
  onSubmit: (formData: Record<string, unknown>) => void;
  onDismiss?: () => void;
}

export default function ChatFormModal({ widget, disabled, onSubmit, onDismiss }: Props) {
  const args = (widget.args ?? {}) as {
    title?: string;
    description?: string;
    schema?: Record<string, unknown>;
    uiSchema?: Record<string, unknown>;
  };

  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [hasError, setHasError] = useState(false);

  const schema = (args.schema ?? { type: 'object', properties: {} }) as RJSFSchema;

  /* Default uiSchema: RadioWidget for enum/oneOf fields. */
  /* oneOf+const/title is JSON Schema native — RJSF reads labels from title directly. */
  const baseUiSchema: UiSchema = buildDefaultUiSchema(schema);
  const uiSchema: UiSchema = {
    ...baseUiSchema,
    ...(args.uiSchema as UiSchema | undefined),
    'ui:submitButtonOptions': { norender: true },
  };

  const handleChange = (e: IChangeEvent) => {
    setFormData((e.formData as Record<string, unknown>) ?? {});
    setHasError(false);
  };

  const handleSubmit = (e: IChangeEvent) => {
    if (disabled) return;
    onSubmit((e.formData as Record<string, unknown>) ?? formData);
  };

  const handleValidateAndSubmit = () => {
    if (disabled) return;
    /* Let RJSF validate by triggering the hidden submit. */
    const btn = document.getElementById('rjsf-submit-trigger');
    if (btn) btn.click();
  };

  return (
    <Dialog.Root open onOpenChange={(o) => { if (!o && onDismiss) onDismiss(); }} disablePointerDismissal={!onDismiss}>
      <Dialog.Portal>
        <Dialog.Viewport className="fixed inset-0 z-[1000] bg-[rgba(0,0,0,0.45)] flex items-center justify-center [padding:max(16px,env(safe-area-inset-top))_max(16px,env(safe-area-inset-right))_max(16px,env(safe-area-inset-bottom))_max(16px,env(safe-area-inset-left))] box-border overflow-y-auto">
          <Dialog.Popup className="bg-background rounded-[20px] shadow-[0_12px_48px_rgba(0,0,0,0.28)] p-[28px_24px_24px] w-full max-w-[480px] flex flex-col gap-0 max-h-[88dvh] overflow-y-auto">
            <div className={cn("flex items-start justify-between", (args.title || args.description) && "mb-5")}>
              <div className="flex-1 min-w-0">
                {args.title && <div className="text-[18px] font-black text-foreground leading-[1.3]">{args.title}</div>}
                {args.description && <div className="text-[13px] text-muted-foreground mt-[7px] leading-[1.55]">{args.description}</div>}
              </div>
              {onDismiss && (
                <button type="button" onClick={onDismiss} aria-label="Fermer" className="flex-none ml-3 -mr-2 -mt-2 w-11 h-11 rounded-full border-none bg-naia-info-bg text-muted-foreground cursor-pointer grid place-items-center font-[inherit]">
                  <X size={20} weight="bold" />
                </button>
              )}
            </div>

            <div className="rjsf-naia">
              <Form
                schema={schema}
                uiSchema={uiSchema}
                formData={formData}
                validator={validator}
                widgets={CUSTOM_WIDGETS}
                onChange={handleChange}
                onSubmit={handleSubmit}
                onError={() => setHasError(true)}
                disabled={disabled}
              >
                {/* Hidden real submit button — triggered programmatically for validation */}
                <button id="rjsf-submit-trigger" type="submit" className="hidden" />
              </Form>
            </div>

            {hasError && (
              <div className="text-[12px] text-naia-error mt-2">
                Veuillez remplir les champs obligatoires.
              </div>
            )}

            <button
              type="button"
              disabled={disabled}
              onClick={handleValidateAndSubmit}
              className="mt-5 px-5 py-[14px] rounded-sm border-none bg-naia-blue text-card font-extrabold text-[15px] cursor-pointer font-[inherit] min-h-[50px] w-full shadow-card disabled:bg-naia-info-bg disabled:text-muted-foreground disabled:cursor-default disabled:shadow-none"
            >
              {disabled ? 'Envoi…' : 'Valider'}
            </button>
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/* Build uiSchema: RadioWidget for oneOf/enum/anyOf fields. */
/* oneOf+const/title is JSON Schema native — RJSF reads labels from title directly, */
/* no ui:enumNames injection needed. */
function buildDefaultUiSchema(schema: RJSFSchema): UiSchema {
  const ui: UiSchema = {};
  const props = (schema.properties ?? {}) as Record<string, RJSFSchema>;
  for (const [key, field] of Object.entries(props)) {
    if (field.oneOf || field.anyOf || field.enum) {
      ui[key] = { 'ui:widget': 'RadioWidget' };
    } else if (field.type === 'boolean') {
      ui[key] = { 'ui:widget': 'CheckboxWidget' };
    } else if (field.type === 'string' && ((field.maxLength ?? 0) > 100 || field.format === 'textarea')) {
      ui[key] = { 'ui:widget': 'textarea' };
    }
  }
  return ui;
}
